import { AutoScrollingPhone } from "./AutoScrollingPhone";
import { SectionLabel } from "./sharedUI";

/**
 * Right column showcases a LIVE ReadyTrades build — Gilbert & McCarty.
 * Desktop preview = real screenshot inside a macOS browser frame.
 * Phone preview = tall mobile screenshot auto-scrolling inside the
 * AutoScrollingPhone frame. No mock/fake UI — this is the real site.
 */
const GilbertDesktopPreview = () => (
  <div className="relative rounded-sm overflow-hidden aspect-[16/10] bg-[#0B1C2E] p-3 md:p-4 shadow-[0_30px_60px_-30px_rgba(11,28,46,0.45)]" data-testid="gilbert-desktop-frame">
    <div className="bg-white rounded-md overflow-hidden h-full flex flex-col border border-black/10">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#EDEDED] border-b border-black/10">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="bg-white border border-black/10 rounded-md px-3 py-0.5 text-[10px] md:text-[11px] text-[color:var(--muted)] font-medium tracking-wide flex items-center gap-1.5 max-w-[280px] truncate">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            gilbertandmccarty.co.uk
          </div>
        </div>
        <div className="w-[54px]" />
      </div>
      <div className="flex-1 overflow-hidden bg-white">
        <img
          src="/clients/gilbert-mccarty-desktop.jpg"
          alt="Gilbert & McCarty — a website built by ReadyTrades"
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
      </div>
    </div>
  </div>
);

const GilbertMobileSite = () => (
  <img
    src="/clients/gilbert-mccarty-mobile.jpg"
    alt="Gilbert & McCarty mobile site"
    loading="lazy"
    className="w-full block"
  />
);

export const LookEstablishedSection = () => (
  <section id="what-you-get" className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]" data-testid="benefit-section">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <SectionLabel n="03" name="Benefit / Look Established" />

      <div className="grid grid-cols-12 gap-8 lg:gap-14 items-start">
        {/* Left — copy */}
        <div className="col-span-12 lg:col-span-5">
          <p className="grid-ref mb-4">Benefit 01 of 04</p>
          <h2 className="h-lg text-[color:var(--ink)]">
            Look <span className="annotation">established</span> from day one.
          </h2>
          <p className="text-[16px] leading-[1.6] text-[color:var(--muted)] mt-8 max-w-md">
            A considered logo, a proper domain and a website that reads like a business — not a Facebook page — is the difference between "sending a quote" and "winning the job".
          </p>

          <ul className="mt-10 divide-y divide-[color:var(--border-soft)]">
            {[
              ["Proper logo", "Drawn for your trade, not a template"],
              ["Owned domain", "yourbusiness.co.uk, registered in your name"],
              ["Considered website", "Real photography, service copy, clear pricing"],
            ].map(([title, body]) => (
              <li key={title} className="py-4 grid grid-cols-12 gap-3">
                <span className="col-span-4 font-display text-[16px] text-[color:var(--ink)]">{title}</span>
                <span className="col-span-8 text-[14px] text-[color:var(--muted)]">{body}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex items-center gap-3">
            <span className="stamp stamp-accent">02 / Get found</span>
            <span className="stamp stamp-ink">03 / Win enquiries</span>
            <span className="stamp stamp-ink">04 / Get paid</span>
          </div>
        </div>

        {/* Right — real live client build: Gilbert & McCarty */}
        <div className="col-span-12 lg:col-span-7">
          <figure aria-label="Live ReadyTrades project — Gilbert & McCarty" className="relative">
            <div className="md:pr-[110px] lg:pr-[130px]">
              <GilbertDesktopPreview />
            </div>
            <div className="mt-4 md:mt-0 md:absolute md:right-0 md:-bottom-8" data-testid="gilbert-mobile-preview">
              <div className="ml-auto md:ml-0 w-[124px] md:w-[168px]">
                <AutoScrollingPhone screenHeight={280}>
                  <GilbertMobileSite />
                </AutoScrollingPhone>
              </div>
            </div>
            <figcaption className="mt-6 md:mt-8 flex flex-wrap items-center justify-between gap-3 text-[color:var(--muted)] md:pr-[130px] lg:pr-[190px]">
              <span className="grid-ref">FIG. 02 — Gilbert &amp; McCarty</span>
              <a
                href="https://gilbertandmccarty.co.uk/"
                target="_blank"
                rel="noreferrer"
                className="stamp stamp-ink hover:opacity-80 transition-opacity"
                data-testid="gilbert-visit-link"
              >
                Live build · Visit site
              </a>
            </figcaption>
          </figure>

          {/* Before / after strip — kept because it earns the "look established" benefit */}
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-6 border border-[color:var(--border-soft)] rounded-sm p-4 bg-[color:var(--bg-paper)]">
              <p className="grid-ref mb-2">Before</p>
              <p className="font-serif-italic text-[color:var(--muted)] text-[14px]">Word of mouth &amp; a Facebook page.</p>
              <p className="text-[10px] text-[color:var(--soft)] mt-1">No website · No local search presence</p>
            </div>
            <div className="col-span-6 border border-[color:var(--ink)] rounded-sm p-4 bg-[color:var(--ink)] text-white">
              <p className="grid-ref text-white/60 mb-2">After</p>
              <p className="font-display text-white text-[14px] leading-tight">gilbertandmccarty.co.uk</p>
              <p className="text-[10px] text-white/60 mt-1">Live · Desktop &amp; mobile responsive</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
